package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.StandupRunDetailDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlin.time.Duration.Companion.hours

@Composable
fun NoActivityBanner(
    detail: StandupRunDetailDto,
    compact: Boolean = false,
) {
    val end = detail.scheduledFor
    val start = end.minus(24.hours)
    val everyoneAway = detail.leave.isNotEmpty()
    val breakdown = QuietRunRules.breakdown(detail.sections)
    val headline = when {
        compact -> "Some people could not be read for this run"
        QuietRunRules.everyoneUnlinked(detail.sections) -> "Nobody on this standup is linked to Linear"
        everyoneAway -> "Everyone on the roster is away today"
        else -> "No Linear activity in the fetch window"
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(Palette.accentSubtle)
            .borderRadius(Radius.md)
            .padding(Spacing.md)
            .styleModifier {
                property("border", "1px solid ${Palette.accentBorder.cssRgb()}")
            },
        verticalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
        ) {
            SpanText(
                text = "🤫",
                modifier = Modifier.fontSize(Typography.h3.size),
            )
            SpanText(
                text = headline,
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(Palette.textPrimary),
            )
        }
        breakdown?.let { DiagLine("This run", it) }
        if (!everyoneAway && !compact) DiagLine("Window fetched", "${formatShort(start)} → ${formatShort(end)}")
        if (!compact) DiagLine("Source", detail.source.name.lowercase())
        if (!compact) detail.errorCode?.let { DiagLine("Code", it) }
        if (!compact) detail.errorMessage?.let { msg ->
            SpanText(
                text = msg,
                modifier = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textPrimary)
                    .styleModifier {
                        property("white-space", "pre-wrap")
                        property("font-family", Typography.FONT_MONO)
                        property("background-color", "rgba(0,0,0,0.04)")
                        property("padding", "8px 12px")
                        property("border-radius", "6px")
                        property("line-height", "1.5")
                    },
            )
        }

        if (!everyoneAway && !compact && breakdown == null) SpanText(
            text = "Common reasons: the members on this standup don't have Linear linked by email yet " +
                "(check the Members tab), or no tickets were touched in the 24-hour window above. " +
                "For backfilling a past day, pick the target date in 'Run for date…' — " +
                "the window covers that entire day.",
            modifier = Modifier
                .margin(top = Spacing.xs)
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary)
                .styleModifier { property("line-height", "1.5") },
        )
    }
}

@Composable
private fun DiagLine(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(Spacing.md),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(500)
                .color(Palette.textSecondary)
                .styleModifier { property("min-width", "140px") },
        )
        SpanText(
            text = value,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textPrimary)
                .styleModifier {
                    property("font-family", Typography.FONT_MONO)
                    property("overflow-wrap", "anywhere")
                },
        )
    }
}

private fun formatShort(instant: kotlinx.datetime.Instant): String {
    val local = instant.toString().substringBefore("Z").replace("T", " ").take(16)
    return "${local}Z"
}
